import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * SectionBand - alternating cream / dark page sections, à la Citwell.
 * tone="cream"  → light background with ink text
 * tone="dark"   → dark Dataleo green with white text
 * tone="white"  → pure white for inline forms
 * tone="mint"   → full mint accent band (rare, big CTAs only)
 */
export const SectionBand = ({
  tone = "cream",
  id,
  testId,
  className = "",
  children,
  padded = true,
}) => {
  const bg =
    tone === "dark"  ? "bg-[#0E1F1A] text-white"      :
    tone === "white" ? "bg-white text-[#0B1210]"      :
    tone === "mint"  ? "bg-[#00A878] text-white"      :
                       "bg-[#F6F3EE] text-[#0B1210]";
  return (
    <section id={id} data-testid={testId} className={`${bg} ${className}`}>
      <div className={`dl-container ${padded ? "py-20 sm:py-28" : ""}`}>
        {children}
      </div>
    </section>
  );
};

/**
 * SectionHeader - kicker + bold black title (Archivo 900).
 * Reused across every band so the typographic rhythm reads as one page.
 */
export const SectionHeader = ({
  kicker,
  title,
  description,
  align = "left",
  tone = "light",
  maxWidth = "max-w-4xl",
}) => {
  const alignCls = align === "center" ? "text-center mx-auto" : "";
  const kickerCls = tone === "dark" ? "text-[#00A878]" : "text-[#00A878]";
  const titleCls  = tone === "dark" ? "text-white" : "text-[#0B1210]";
  const descCls   = tone === "dark" ? "text-white/70" : "text-[#4B5563]";
  return (
    <div className={`${alignCls} ${maxWidth}`}>
      {kicker && (
        <div className={`font-mono-dl text-[11px] tracking-[0.22em] uppercase ${kickerCls}`}>
          {kicker}
        </div>
      )}
      {title && (
        <h2 className={`font-display text-4xl sm:text-5xl lg:text-6xl mt-4 ${titleCls}`}>
          {title}
        </h2>
      )}
      {description && (
        <p className={`mt-6 text-[16px] sm:text-[17px] leading-relaxed max-w-2xl ${descCls}`}>
          {description}
        </p>
      )}
    </div>
  );
};

/**
 * BigCTA - Citwell-style pill button with a trailing arrow.
 */
export const BigCTA = ({
  to,
  href,
  variant = "primary",
  children,
  icon = <ArrowRight size={16} strokeWidth={2.5} />,
  testId,
  ...rest
}) => {
  const cls =
    variant === "outline"       ? "dl-pill dl-pill-outline" :
    variant === "outline-light" ? "dl-pill dl-pill-outline-light" :
    variant === "dark"          ? "dl-pill dl-pill-dark" :
                                  "dl-pill dl-pill-primary";
  const inner = (
    <>
      <span>{children}</span>
      {icon}
    </>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId} className={cls} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={cls} {...rest}>
      {inner}
    </Link>
  );
};

/**
 * ArrowLink - quiet inline link with chevron that grows on hover.
 */
export const ArrowLink = ({ to, href, children, tone = "ink", testId, className = "" }) => {
  const colour = tone === "mint"  ? "text-[#00A878] hover:text-[#00916A]"
               : tone === "light" ? "text-white hover:text-[#00A878]"
                                  : "text-[#0B1210] hover:text-[#00A878]";
  const inner = (
    <>
      <span className="border-b border-current pb-0.5">{children}</span>
      <ArrowRight size={14} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1" />
    </>
  );
  const base = `group inline-flex items-center gap-2 text-[13px] font-bold-dl ${colour} ${className}`;
  return href
    ? <a href={href} data-testid={testId} className={base}>{inner}</a>
    : <Link to={to} data-testid={testId} className={base}>{inner}</Link>;
};
